<script setup lang="ts">
import { SALES_STATUS, getSalesStatusFromIndex, getSalesStatusText, getValueForSegmentedBar } from "~/helpers/modules/sales-helpers";
import { getExternalIdForProperty, getPropertyDaysOnMarket } from "~/helpers/entities/property-helpers";

import { useLayoutStore } from "~/composables/stores/layout";
import { useMutation, useQuery } from "@vue/apollo-composable";
import {
  AssetManagementPropertySubType,
  AssetManagementSaleStatus,
  PortfolioPropertyHeaderDocument,
  UpdatePropertySalesStatusDocument,
} from "~/graphql/generated/graphql";

const route = useRoute();

const layoutStore = useLayoutStore();

const t = useTranslator();

const propertyId = computed(() => route.params.propertyId as string);

const headerQuery = useQuery(PortfolioPropertyHeaderDocument, { id: propertyId.value });

const property = computed(() => headerQuery.result.value?.assetManagementProperty);

const loading = computed(() => headerQuery.loading.value);

const address = computed(() => property.value?.name);
const externalId = computed(() => (property.value ? getExternalIdForProperty(property.value) : undefined));
const salesStatus = computed(() => property.value?.status);
const salesStatusInProgress = computed(() => !(["REMOVED", "NOT_FOR_SALE", "UNKNOWN"] as Nullable<AssetManagementSaleStatus>[]).includes(salesStatus.value));
const daysOnMarket = computed(() => (property.value ? getPropertyDaysOnMarket(property.value) : undefined));
const salesStatusses = computed(() => {
  return Object.keys(SALES_STATUS).map((key, index) => {
    return {
      value: getValueForSegmentedBar(key as AssetManagementSaleStatus),
      key: key as AssetManagementSaleStatus,
    };
  });
});

const updateSalesStatusMutation = useMutation(UpdatePropertySalesStatusDocument);

const updateSalesStatus = (status: number) => {
  updateSalesStatusMutation.mutate({ input: { id: propertyId.value, status: getSalesStatusFromIndex(status) as AssetManagementSaleStatus } });
};

const startSaleProcess = () => {
  updateSalesStatusMutation.mutate({ input: { id: propertyId.value, status: "PREPARING_FOR_SALE" as AssetManagementSaleStatus } });
};

const tabs = useUrlTabsProperty(property);

usePageTitle({ position: 1, title: () => property.value?.name });

watch(
  () => property.value,
  () => {
    if (property.value?.name) {
      useBreadcrumbs().setDynamicLabel("propertyId", property.value.name);
    }
  },
  { immediate: true }
);
</script>

<template>
  <div :class="$style.container">
    <Composition>
      <template #layouts>
        <CompositionLayoutSingle omitPadding>
          <template #container>
            <Header v-if="!loading && property" colorBackground="--color-accent-100" :property="property" />
          </template>
        </CompositionLayoutSingle>
      </template>
    </Composition>

    <Composition>
      <template #layouts>
        <CompositionLayoutSingle omitPaddingBottom omitPaddingTop>
          <template #container>
            <WidgetLayout outline>
              <template #widgetRows>
                <WidgetLayoutRowCellSingle>
                  <template #widget>
                    <WidgetBlank v-if="!loading" omitPaddingContent omitPaddingTop omitClickMore>
                      <template #container>
                        <EntityCard
                          v-if="!loading"
                          :colorBackground="'--color-white'"
                          :colorForeground="'--color-primary'"
                          :colorSeperators="'--color-accent-200'"
                          :alignControlsEnd="!salesStatusInProgress ? true : false"
                        >
                          <template #header>
                            <KeyValuePair direction="horizontal">
                              <template #containerStart>
                                <Icon id="icon-property-tt" size="big" color="--color-primary" />
                              </template>

                              <template #containerEnd>
                                <Copy type="header" size="large" color="--color-primary"> {{ address }} </Copy>
                              </template>
                            </KeyValuePair>
                          </template>

                          <template #byline>
                            <div style="display: flex; flex-direction: row; padding: 0.5rem 0rem; gap: 1rem">
                              <KeyValuePair direction="horizontal" renderAsLabel labelColorNamespace="accent">
                                <template #containerStart>
                                  <Copy type="label" size="small" color="--color-primary">
                                    {{ t("PORTFOLIO_TYPE") }}
                                  </Copy>
                                </template>

                                <template #containerEnd>
                                  <Copy type="body" size="small" color="--color-primary">
                                    {{ property?.usage ? property.usage : t("PORTFOLIO_PROPERTY_NO_USAGE") }}
                                  </Copy>
                                </template>
                              </KeyValuePair>

                              <KeyValuePair direction="horizontal" renderAsLabel labelColorNamespace="accent">
                                <template #containerStart>
                                  <Copy type="label" size="small" color="--color-primary">{{ t("PORTFOLIO_PROPERTY_EXTERNAL_ID") }}</Copy>
                                </template>

                                <template #containerEnd>
                                  <Copy type="number" size="small" color="--color-primary" wrap>
                                    {{ externalId }}
                                  </Copy>
                                </template>
                              </KeyValuePair>
                            </div>
                          </template>

                          <template #controlsEnd v-if="property?.subType === AssetManagementPropertySubType.Property">
                            <div v-if="salesStatusInProgress" style="display: flex">
                              <KeyValuePair direction="vertical">
                                <template #containerStart>
                                  <Copy type="label" size="small" color="--color-primary">{{ t("PORTFOLIO_PROPERTY_SALES_STATUS") }}</Copy>
                                </template>

                                <template #containerEnd>
                                  <SegmentedDropDown
                                    :modelValue="getValueForSegmentedBar(salesStatus)"
                                    :label="salesStatus ? getSalesStatusText(salesStatus) : t('PORTFOLIO_PROPERTY_SELECT_STATUS')"
                                    colorName="green"
                                    minWidth="12rem"
                                    maxWidth="12rem"
                                    :valueIsLessThanIndex="true"
                                    @update:modelValue="updateSalesStatus"
                                  >
                                    <DropDownItem
                                      v-for="status in salesStatusses"
                                      :key="status.value"
                                      :label="getSalesStatusText(status.key)"
                                      :value="status.value"
                                    />
                                  </SegmentedDropDown>
                                </template>
                              </KeyValuePair>

                              <CompositionSpacer direction="horizontal" size="medium" />

                              <KeyValuePair direction="vertical">
                                <template #containerStart>
                                  <Copy type="label" size="small" color="--color-primary">{{ t("PORTFOLIO_PROPERTY_DAYS_ON_MARKET") }}</Copy>
                                </template>

                                <template #containerEnd>
                                  <Copy type="number" size="huge" color="--color-primary"> {{ daysOnMarket }} </Copy>
                                </template>
                              </KeyValuePair>
                            </div>

                            <div v-else>
                              <PushButton size="medium" color="--color-transparent" underline :disabled="loading" @click="startSaleProcess">
                                <template #iconBefore>
                                  <Icon id="add" size="medium" color="--color-primary" />
                                </template>

                                <template #copy>
                                  <Copy type="bodyStrong" size="small" color="--color-primary">{{ t("PORTFOLIO_PROPERTY_START_SALES_STATUS") }}</Copy>
                                </template>
                              </PushButton>
                            </div>
                          </template>
                        </EntityCard>
                      </template>
                    </WidgetBlank>
                  </template>
                </WidgetLayoutRowCellSingle>

                <WidgetLayoutRowCellSingle>
                  <template #widget>
                    <WidgetBlank omitPaddingContent omitPaddingTop omitClickMore>
                      <template #container>
                        <UrlTabContainer v-if="!!property" :items="tabs" basePath="portfolio-property-propertyId" context="property" :params="{ propertyId }" />
                      </template>
                    </WidgetBlank>
                  </template>
                </WidgetLayoutRowCellSingle>
              </template>
            </WidgetLayout>
          </template>
        </CompositionLayoutSingle>
      </template>
    </Composition>

    <Composition :class="[layoutStore.widgetScroll && $style.widgetScroll]">
      <template #layouts>
        <CompositionLayoutSingle>
          <template #container>
            <WidgetLayout outline>
              <template #widgetRows>
                <slot />
              </template>
            </WidgetLayout>
          </template>
        </CompositionLayoutSingle>
      </template>
    </Composition>
  </div>
</template>

<style module>
.container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.widgetScroll {
  overflow: hidden;
}
</style>
